<script setup lang="ts">
    type Height = NonNullable<SlideshowStoryblok['height']>;

    type SlideshowClass = 'slideshow' | `slideshow-${Exclude<Height, ''>}`;

    const props = defineProps<{
        media: StoryblokAsset;
        type: 'productCard' | 'mainPicture' | SlideshowClass | 'memoryCard' | 'icon' | 'banner';
        alt?: string;
        quality?: number;
        eager?: boolean;
        ariaHidden?: boolean;
        zoom?: boolean;
    }>();

    const dimensions = {
        width: Number.parseInt(props.media?.filename!.split('/')[5]!.split('x')[0]!),
        height: Number.parseInt(props.media?.filename!.split('/')[5]!.split('x')[1]!)
    };

    const MAX_IMAGE_WIDTH = dimensions.width < 1920 ? dimensions.width : 1920;
    const MIN_IMAGE_WIDTH = 80;

    function buildUrl(baseUrl: string, targetSize: number, quality = 90): string {
        let imageSettings = '';

        const isLandscape = dimensions.width >= dimensions.height;
        // since our images are squares we should use the short side of the image for generating a size
        if (isLandscape) {
            imageSettings = `0x${targetSize}`;
        } else {
            imageSettings = `${targetSize}x0`;
        }

        return `${baseUrl}/m/${imageSettings}/filters:quality(${quality}):focal(${props.media?.focus})`;
    }

    // target size in px – used to generate widths
    const targetSizeMap: Record<typeof props.type, number> = {
        productCard: 460,
        mainPicture: 600,
        banner: 1800,
        'slideshow-l': 900,
        'slideshow-m': 600,
        'slideshow-s': 400,
        slideshow: 600,
        memoryCard: 121,
        icon: 200
    };

    const sizeMap: Record<typeof props.type, string> = {
        productCard: '(min-width: 1280px) 460px, (min-width: 1020px) 50vw, 100vw',
        mainPicture: '(min-width: 1280px) 30vw, (min-width: 768px) 600px, 98vw',
        banner: '100vw',
        'slideshow-l': '900px',
        'slideshow-m': '600px',
        'slideshow-s': '400px',
        slideshow: '100vw',
        memoryCard: '(min-width: 1280px) 121px, 100vw',
        icon: '200px'
    };

    const sizes = sizeMap[props.type] ?? '100vw';
    const base = targetSizeMap[props.type];
    const quality = props.quality ?? 90;

    // generate multiples for high pixel density displays
    const scaleFactors = [0.5, 0.75, 1, 1.5, 2, 2.5, 3];

    const scaledWidths = scaleFactors.map((factor) => Math.round(base * factor)); // scale base size
    const uniqueWidths = Array.from(new Set(scaledWidths)); // remove duplicates
    const widths = uniqueWidths.filter((width) => width <= MAX_IMAGE_WIDTH && width >= MIN_IMAGE_WIDTH); // clamp width

    let srcset: string;
    if (props.media) {
        srcset = widths.map((w) => `${buildUrl(props.media.filename!, w, quality)} ${w}w`).join(', ');
    }

    let focusPointPercentX: number;
    let focusPointPercentY: number;
    let focusPoint: string;
    if (props.media?.focus) {
        const focusString = props.media?.focus.split(':')[0]!;
        const [focusX, focusY] = focusString.split('x');
        focusPointPercentX = (Number.parseInt(focusX) / dimensions.width) * 100 || 50;
        focusPointPercentY = (Number.parseInt(focusY) / dimensions.height) * 100 || 50;
        focusPoint = `${focusPointPercentX}% ${focusPointPercentY}%`;
    }
</script>

<template>
    <img
        v-if="!zoom"
        :src="buildUrl(props.media?.filename!, base, quality)"
        :alt="props.alt || props.media?.alt || ''"
        :srcset="srcset"
        :sizes="sizes"
        :style="{ objectPosition: props.media?.focus ? focusPoint : '' }"
        :loading="props.eager ? undefined : 'lazy'"
        :fetchpriority="props.eager ? 'high' : undefined"
        decoding="async"
        :aria-hidden="props.ariaHidden ? props.ariaHidden : 'false'"
    />

    <ClientOnly v-else>
        <VueImageZoomer
            :click-zoom="true"
            :show-message="false"
            :zoom-amount="3"
            :regular="buildUrl(props.media?.filename!, base, quality)"
            :zoom="buildUrl(props.media?.filename!, base, quality)"
            :regular-webp="buildUrl(props.media?.filename!, base, quality)"
            :zoom-webp="buildUrl(props.media?.filename!, base, quality)"
            :alt="props.alt || props.media?.alt || ''"
        />
    </ClientOnly>
</template>
